<template>
  <button
    :disabled="props.option.disabled"
    :class="bem({
        selected: props.selected,
        preorder: !props.option.inStock && props.option.preorder,
        soldOut: !props.option.inStock && !props.option.preorder,
        error: props.error,
        extendedLayout: props.extendedLayout
    })"
    @click="emits('selected', option)"
  >
    <div
      v-if="tagText && props.extendedLayout"
      :class="bem('extended-layout-tag-wrapper')"
    >
      <VisionTag
        size="x-small"
        :outlined="true"
        :text="tagText"
        theme="default"
        :class="bem('tag')"
      />
    </div>

    <div :class="bem('title-wrapper')">
      <div
        v-if="['8ko', '8kop', '8kosnow'].includes(props.option.tech)"
        :class="bem('icon-wrapper')"
      >
        <div
          v-if="'8kop' === props.option.tech"
          :class="bem('icon-with-text')"
        >
          <VisionIcon
            icon="8ko"
            colour="black"
            size="stretch"
            :class="bem('icon', { '8ko': true })"
          />
          <span :class="bem('icon-text')">
            {{ props.option?.lensTechSelectorTitleOverride }}
          </span>
        </div>
        <div
          v-else-if="'8ko' === props.option.tech"
          :class="bem('icon-with-text')"
        >
          <VisionIcon
            icon="8ko"
            colour="black"
            size="stretch"
            :class="bem('icon', { '8ko': true })"
          />
          <span :class="bem('icon-text')">
            &nbsp;
          </span>
        </div>
        <VisionIcon
          v-else-if="'8kosnow' === props.option.tech"
          icon="8kosnow"
          colour="black"
          size="stretch"
          :class="bem('icon', { '8kosnow': true })"
        />
        <div :class="bem('tag-wrapper')">
          <VisionTag
            v-if="tagText && !props.extendedLayout"
            size="x-small"
            :outlined="true"
            :text="tagText"
            :theme="(!props.option.inStock || props.option.preorder) ? 'white' : 'default'"
            :class="bem('tag')"
          />
        </div>
      </div>
      <!-- eslint-disable vue/no-v-html -->
      <div
        v-else
        :class="bem('inner-title-wrapper')"
      >
        <span
          
          :class="bem('title')"
          v-html="props.option.lensTechSelectorTitleOverride || props.option.displayTitle"
        />
        <div :class="bem('tag-wrapper')">
          <VisionTag
            v-if="tagText"
            size="x-small"
            :outlined="true"
            :text="tagText"
            :theme="(!props.option.inStock || props.option.preorder) ? 'white' : 'default'"
            :class="bem('tag')"
          />
      </div>
    </div>
      <span v-if="priceDisplayOverride || calculatedPrice" :class="bem('price')">
        {{ priceDisplayOverride || calculatedPrice }}
      </span>
      <!-- eslint-enable vue/no-v-html -->
    </div>
    <!-- eslint-disable vue/no-v-html -->
    <p
      v-if="!props.extendedLayout && (props.subtitle || props.option.displaySubtitle)"
      :class="bem('subtitle')"
      v-html="props.subtitle || props.option.displaySubtitle"
    />
    <!-- eslint-enable vue/no-v-html -->

    <StructuredText
      v-if="props.extendedLayout && props.option.optionTech.extendedContent"
      :class="bem('extended-content', { isPolarised: ['8kop'].includes(props.option.optionTech.tech) })"
      :data="props.option.optionTech.extendedContent"
    />
  </button>
</template>

<script setup lang="ts">
import { StructuredText } from 'vue-datocms'

interface Props {
  option: {
    tagText?: string
    inStock: boolean
    preorder: boolean
    disabled: boolean
    isLimitedEdition: boolean
    price: number
    priceGBP: number
    displayTitle: string
    displaySubtitle: string
    optionTech: {
      tech: string
      tagText: string
      extendedContent: string
    }
  }
  error: boolean,
  activeOption: object
  selected: boolean
  priceOffset?: number
  subtitle?: string
  priceDisplayOverride?: string
  extendedLayout?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  option: () => ({
    inStock: true,
    preorder: false,
    isLimitedEdition: false,
    disabled: false,
    price: 0,
    priceGBP: 0,
    displayTitle: '',
    displaySubtitle: '',
    optionTech: {
      tech: '',
      tagText: '',
      extendedContent: ''
    }
  }),
  error: false,
  activeOption: () => ({}),
  disabled: false,
  selected: false,
  priceOffset: 0,
  subtitle: '',
  priceDisplayOverride: '',
  extendedLayout: false
})

const { $tcs } = useNuxtApp()
const bem = useBEM('LensTechOption')
const emits = defineEmits(['selected'])

const tagText = computed(() => {
  if (!props.option.inStock && !props.option.preorder) {
    return 'Sold out'
  }

  if (!props.option.inStock && props.option.preorder) {
    return 'Preorder'
  }

  if (props.option.optionTech.tagText) {
    return props.option.optionTech.tagText
  }

  return ''
})

const calculatedPrice = computed(() => {
  if (!props.option.price) return null

  if (props.priceOffset < 0) {
    return $tcs(props.option.price + props.priceOffset)
  }

  return $tcs(props.option.price)
})
</script>

<style lang="postcss">
.LensTechOption {
  @apply py-5 px-4
    flex flex-col
    bg-white
    border border-grey-400
    rounded-lg
    text-left
    w-full
    relative;

  &:hover:not(:disabled) {
    @apply border-grey-500;
  }

  &:disabled {
    @apply border-grey-400 cursor-not-allowed;

    .LensTechOption__price {
      @apply text-grey-500;
    }
  }
}

.LensTechOption__extended-layout-tag-wrapper {
  @apply absolute top-0 left-2
    -translate-y-1/2
    transform
    flex items-center justify-center;
}

.LensTechOption__tag-wrapper {
  @apply flex;
}

.LensTechOption__tag {
  --vision-tag-border-color: theme('borderColor.grey.400');

  .is-dark-chrome-theme & {
    --vision-tag-border-color: theme('borderColor.grey.700');
    --vision-tag-background-color: theme('colors.black');
    --vision-tag-text-color: theme('colors.white')
  }
}

.LensTechOption__title-wrapper {
  @apply flex justify-between items-start
    w-full
    leading-4
    mb-1;
}

.LensTechOption__title {
  @apply font-bold text-sm flex-shrink-0;
}

.LensTechOption__subtitle {
  @apply text-grey-700 text-[13px] md:text-xxs leading-4;
}

.LensTechOption__price {
  @apply font-semibold text-sm lg:text-[13px];
}

.LensTechOption__inner-title-wrapper,
.LensTechOption__icon-wrapper {
  @apply text-left flex-shrink-0 flex items-start gap-3;
}

.LensTechOption__button-icon {
  @apply h-4 max-w-full;
}

.LensTechOption__button-icon--snow {
  @apply h-3;
}

.LensTechOption__button-icon-text {
  @apply font-bold text-[11px] md:text-xxs;
}

.LensTechOption__preorder {
  @apply whitespace-nowrap text-[10px] uppercase text-grey-600;
}

.LensTechOption__icon-wrapper {
  @apply flex;
}

.LensTechOption__icon--8ko {
  @apply w-10 h-4;
}

.LensTechOption__icon--8kosnow {
  @apply w-20 h-4 overflow-hidden;
}

.LensTechOption__icon-with-text {
  .LensTechOption__icon {
    @apply overflow-hidden;
  }
}

.LensTechOption__icon-text {
  @apply block font-semibold text-tiny;

  .LensTechOption--extended-layout & {
    @apply text-[15px];
  }
}

/**
  Modifiers
*/
.LensTechOption--selected:not(:disabled),
.LensTechOption--selected:hover:not(:disabled) {
  @apply border-grey-800;

  .LensTechOption__price {
    @apply t-gradient;
  }
}

.LensTechOption--selected.LensTechOption--preorder:not(:disabled) {
  .LensTechOption__tag {
    --vision-tag-text-color: theme('colors.grey.600');
  }
}

.LensTechOption--preorder:not(:disabled) {
  .LensTechOption__tag {
    --vision-tag-text-color: theme('colors.grey.600');
  }
}

.LensTechOption:disabled {
  @apply border-grey-200;

  .LensTechOption__title,
  .LensTechOption__subtitle {
    @apply text-grey-500;
  }

  .LensTechOption__icon,
  .LensTechOption__icon-text {
    @apply opacity-30;
  }

  .LensTechOption__tag {
    --vision-tag-text-color: theme('borderColor.grey.500');
    --vision-tag-border-color: theme('borderColor.grey.200');
  }
}

.LensTechOption--error:not(:disabled) {
  @apply border-red;
}

/* Dark Chrome Theme */
.is-dark-chrome-theme {
  .LensTechOption {
    @apply bg-transparent;
  }

  .LensTechOption__icon {
    --vision-icon-fill: white;
  }
}

.LensTechOption__extended-content {
  @apply text-[13px] md:text-xxs leading-4
    flex flex-col gap-2 mt-2;

  > p {
    @apply text-[10px] md:text-xxs;
  }

  ul {
    @apply list-none;

    li {
      @apply list-none ml-4 text-[13px] leading-relaxed;

      p {
        @apply inline-block;
      }
    }
    
    li::before{
      @apply content-[''] inline-block -ml-4 mr-2 grayscale opacity-50 absolute translate-y-1/2;
      width: 12px;
      height: 12px;
      background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iMTIiIHZpZXdCb3g9IjAgMCAxMiAxMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTQuMzIxNDggMTEuMDgzN0M0LjEwODA0IDExLjA4MzcgMy45MDc5NSAxMS4wMDM2IDMuNzYxMjIgMTAuODU3TDAuMjY2Mjk1IDcuMjU2NjZDLTAuMDY3MTkwNCA2Ljk0OTk3IC0wLjA4MDUyOTggNi40Mjk5MiAwLjIzOTYxNiA2LjA5NjU2QzAuNTQ2NDIyIDUuNzYzMiAxLjA2NjY1IDUuNzQ5ODYgMS40MDAxNCA2LjA2OTg5TDMuOTYxMzEgNy44OTY3MkM0LjEwODA0IDguMDMwMDYgNC4zMzQ4MSA4LjAwMzM5IDQuNDU0ODcgNy44NDMzOEwxMC41MjQzIDAuMzQ5Mzk3QzEwLjc5MSAtMC4wMTA2MzM5IDExLjMxMTMgLTAuMDkwNjQwOSAxMS42NzE1IDAuMTg5Mzg0QzEyLjAzMTYgMC40NTYwNzMgMTIuMTExNyAwLjk3NjExOSAxMS44MzE2IDEuMzM2MTVMNC45NzUxIDEwLjc1MDNDNC44MjgzNyAxMC45MzcgNC42MTQ5NCAxMS4wNTcgNC4zODgxNyAxMS4wNzAzQzQuMzYxNDkgMTEuMDgzNyA0LjM0ODE2IDExLjA4MzcgNC4zMjE0OCAxMS4wODM3WiIgZmlsbD0idXJsKCNwYWludDBfbGluZWFyXzc4XzUwNCkiLz4KPGRlZnM+CjxsaW5lYXJHcmFkaWVudCBpZD0icGFpbnQwX2xpbmVhcl83OF81MDQiIHgxPSIwLjAwNzYxNDE2IiB5MT0iMTAuNjQ0MyIgeDI9IjEzLjcxMTYiIHkyPSI1LjIzOTE5IiBncmFkaWVudFVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+CjxzdG9wIHN0b3AtY29sb3I9IiM1QzBGOTkiLz4KPHN0b3Agb2Zmc2V0PSIwLjYiIHN0b3AtY29sb3I9IiMzNDQ5QjgiLz4KPHN0b3Agb2Zmc2V0PSIxIiBzdG9wLWNvbG9yPSIjMTc5OUUxIi8+CjwvbGluZWFyR3JhZGllbnQ+CjwvZGVmcz4KPC9zdmc+Cg==);
    }
  }

  &--is-polarised {
    ul li::before {
      @apply grayscale-0 opacity-100;
    }
  }

  blockquote {
    @apply p-3 text-center relative font-medium rounded-[4px];
    background: #F6FBFE;

    p {
      @apply t-gradient;
    }
  }
}
</style>
